/* eslint-disable @typescript-eslint/no-explicit-any */
import axios from 'axios';
import { call, put, takeEvery } from 'redux-saga/effects';
import { PAGE_SIZE } from 'services/constants/env';
import { api } from 'services/api';
import { notificationContainer } from 'utils/notificationContainer';
import { PayloadAction } from '@reduxjs/toolkit';
import fileDownload from 'js-file-download';

import {
	TGpsrRegParseResponsePayload,
	TGpsrRegisterRequest,
	TGpsrRegParseRequest,
	TGpsrPlansResponsePayload,
	TGpsrItemsResponsePayload,
	TGpsrItemsRequest,
	TGpsrProductTypesResponsePayload,
	IGpsrProductTypesRequest,
	TGenerateGpsrInvoiceRequest,
	TGpsrRegulationsRequest,
	TGpsrRegulationsResponsePayload,
	TAddGpsrProductTypeRequest,
	TAddGpsrRegulationsRequest,
	TDeleteGpsrProductTypeRequest,
	TDeleteGpsrRegulationsRequest,
	TGpsrEditCustomerRequest,
	TGetProductTypeReferencesRequest,
	TGetProductTypeReferencesResponsePayload,
	TGetProductTypeRegulationsGroupTypesRequest,
	TGetProductTypeRegulationsGroupTypesResponsePayload,
	TGetProductTypeRegulationsReferencesRequest,
	TGetProductTypeRegulationsReferencesResponsePayload,
	TGetRegulationsReferencesRequest,
	TGetRegulationsReferencesResponsePayload,
	TGetTechFileRequest,
	TGetTechFileResponsePayload,
	TGetDocFileRequest,
	TGetDocFileResponsePayload,
	TCancelGpsrRequest,
	TActivateGpsrRequest,
	TDownloadGpsrInvoiceRequest,
	TDownloadGpsrInvoiceResponsePayload,
	TGetInvoicesListRequest,
	TGetInvoicesListResponsePayload,
	TDownloadGpsrLastInvoiceRequest,
	TDownloadGpsrLastInvoiceResponsePayload,
	TUpdateGpsrServiceSettingsRequest,
	TUpdateGpsrRemoveOnRenewalRequest,
	TChangeGpsrStatusRequest,
	TGpsrServiceStatusesResponsePayload,
	TGpsrReferralRequest,
	TChangeGpsrStatusResponsePayload,
} from './types';
import {
	gpsrInitState,
	regParseGpsrRequest,
	regParseGpsrSuccess,
	regParseGpsrFailure,
	registerGpsrRequest,
	registerGpsrSuccess,
	registerGpsrFailure,
	getGpsrPlansRequest,
	getGpsrPlansSuccess,
	getGpsrItemsRequest,
	getGpsrItemsSuccess,
	getGpsrProductTypesRequest,
	getGpsrProductTypesSuccess,
	generateInvoiceGpsrRequest,
	generateInvoiceGpsrSuccess,
	getGpsrRegulationsRequest,
	getGpsrRegulationsSuccess,
	addGpsrProductTypeRequest,
	deleteGpsrProductTypeRequest,
	addGpsrRegulationsRequest,
	deleteGpsrRegulationsRequest,
	editGpsrCustomerRequest,
	editGpsrCustomerSuccess,
	getProductTypeReferencesRequest,
	getProductTypeReferencesSuccess,
	getProductTypeRegulationsGroupTypesRequest,
	getProductTypeRegulationsGroupTypesSuccess,
	getProductTypeRegulationsReferencesRequest,
	getProductTypeRegulationsReferencesSuccess,
	getRegulationsReferencesRequest,
	getRegulationsReferencesSuccess,
	getTechFileRequest,
	getTechFileSuccess,
	getDocFileSuccess,
	getDocFileRequest,
	cancelGpsrSuccess,
	activateGpsrSuccess,
	cancelGpsrRequest,
	activateGpsrRequest,
	downloadGpsrLastInvoiceSuccess,
	downloadGpsrLastInvoiceRequest,
	getGpsrInvoicesListSuccess,
	getGpsrInvoicesListRequest,
	downloadGpsrInvoiceSuccess,
	downloadGpsrInvoiceRequest,
	updateGpsrServiceSettingsRequest,
	updateGpsrServiceSettingsSuccess,
	updateGpsrRemoveOnRenewalRequest,
	updateGpsrRemoveOnRenewalSuccess,
	changeGpsrStatusRequest,
	changeGpsrStatusSuccess,
	getGpsrServiceStatusesRequest,
	getGpsrServiceStatusesSuccess,
	getGpsrServiceStatusesFailure,
	gpsrReferralRequest,
	gpsrReferralSuccess,
	gpsrReferralFailure,
} from './reducer';

export function* regParseGpsrRequestWorker(action: PayloadAction<TGpsrRegParseRequest>) {
	const { payload } = action;
	try {
		const response: TGpsrRegParseResponsePayload = yield call(api.gpsr.regParseGpsr, payload);
		yield put(regParseGpsrSuccess(response));
	} catch (error) {
		if (axios.isAxiosError(error)) {
			notificationContainer(String(error?.response?.data?.message ?? error ?? 'Error!'), 'error');
			yield put(
				regParseGpsrFailure({ error: String(error?.response?.data?.message ?? error ?? 'Error!') }),
			);
		}
	}
}

export function* registerGpsrRequestWorker(action: PayloadAction<TGpsrRegisterRequest>) {
	const { payload } = action;
	try {
		yield call(api.gpsr.registerGpsr, payload);
		yield put(registerGpsrSuccess());
		notificationContainer('Registration successful', 'success');
		if (payload.emId) {
			yield put(getGpsrProductTypesRequest({ emId: payload.emId, searchString: '' }));
		} else {
			setTimeout(() => {
				window.location.reload();
			}, 1000);
		}
	} catch (error) {
		if (axios.isAxiosError(error)) {
			notificationContainer(String(error?.response?.data?.message ?? error ?? 'Error!'), 'error');
			yield put(
				registerGpsrFailure({ error: String(error?.response?.data?.message ?? error ?? 'Error!') }),
			);
		}
	}
}

export function* editGpsrCustomerRequestWorker(action: PayloadAction<TGpsrEditCustomerRequest>) {
	const { payload } = action;
	try {
		yield call(api.gpsr.editGpsrCustomer, payload);
		yield put(editGpsrCustomerSuccess());
		notificationContainer('Customer edited successfully', 'success');
		if (payload.emId) {
			yield put(getGpsrProductTypesRequest({ emId: payload.emId, searchString: '' }));
		} else {
			setTimeout(() => {
				window.location.reload();
			}, 1000);
		}
	} catch (error) {
		if (axios.isAxiosError(error)) {
			notificationContainer(String(error?.response?.data?.message ?? error ?? 'Error!'), 'error');
		}
	}
}

export function* getGpsrPlansRequestWorker() {
	try {
		const response: TGpsrPlansResponsePayload = yield call(api.gpsr.getGpsrPlans);
		yield put(getGpsrPlansSuccess(response));
	} catch (error) {
		if (axios.isAxiosError(error)) {
			notificationContainer(String(error?.response?.data?.message ?? error ?? 'Error!'), 'error');
		}
	}
}

export function* getGpsrItemsRequestWorker(action: PayloadAction<TGpsrItemsRequest>) {
	const { payload } = action;
	try {
		const response: TGpsrItemsResponsePayload = yield call(api.gpsr.getGpsrItems, payload);
		yield put(getGpsrItemsSuccess(response));
	} catch (error) {
		if (axios.isAxiosError(error)) {
			notificationContainer(String(error?.response?.data?.message ?? error ?? 'Error!'), 'error');
		}
	}
}

export function* getGpsrProductTypesRequestWorker(action: PayloadAction<IGpsrProductTypesRequest>) {
	const { payload } = action;
	try {
		const response: TGpsrProductTypesResponsePayload = yield call(
			api.gpsr.getGpsrProductTypes,
			payload,
		);
		yield put(getGpsrProductTypesSuccess(response));
	} catch (error) {
		if (axios.isAxiosError(error)) {
			notificationContainer(String(error?.response?.data?.message ?? error ?? 'Error!'), 'error');
		}
	}
}

function* generateInvoiceGpsrRequestWorker(action: PayloadAction<TGenerateGpsrInvoiceRequest>) {
	const { payload } = action;
	try {
		yield call(api.gpsr.generateInvoiceGpsr, payload);
		yield put(generateInvoiceGpsrSuccess());
		notificationContainer('Invoice generated successfully', 'success');
		setInterval(() => {
			window.location.reload();
		}, 1000);
	} catch (error) {
		if (axios.isAxiosError(error)) {
			notificationContainer(String(error?.response?.data?.message ?? error ?? 'Error!'), 'error');
		}
	}
}

function* getGpsrRegulationsRequestWorker(action: PayloadAction<TGpsrRegulationsRequest>) {
	const { payload } = action;
	try {
		const response: TGpsrRegulationsResponsePayload = yield call(
			api.gpsr.getGpsrRegulations,
			payload,
		);
		yield put(getGpsrRegulationsSuccess(response));
	} catch (error) {
		if (axios.isAxiosError(error)) {
			notificationContainer(String(error?.response?.data?.message ?? error ?? 'Error!'), 'error');
		}
	}
}

function* addGpsrProductTypeRequestWorker(action: PayloadAction<TAddGpsrProductTypeRequest>) {
	const { payload } = action;
	try {
		yield call(api.gpsr.addGpsrProductType, payload);
		notificationContainer('Product type added successfully', 'success');
		yield put(getGpsrProductTypesRequest(payload.request));
	} catch (error) {
		if (axios.isAxiosError(error)) {
			notificationContainer(String(error?.response?.data?.message ?? error ?? 'Error!'), 'error');
		}
	}
}

function* deleteGpsrProductTypeRequestWorker(action: PayloadAction<TDeleteGpsrProductTypeRequest>) {
	const { payload } = action;
	try {
		yield call(api.gpsr.deleteGpsrProductType, payload);
		notificationContainer('Product type deleted successfully', 'success');
		yield put(getGpsrProductTypesRequest(payload.request));
	} catch (error) {
		if (axios.isAxiosError(error)) {
			notificationContainer(String(error?.response?.data?.message ?? error ?? 'Error!'), 'error');
		}
	}
}

function* addGpsrRegulationsRequestWorker(action: PayloadAction<TAddGpsrRegulationsRequest>) {
	const { payload } = action;
	try {
		yield call(api.gpsr.addGpsrRegulations, payload);
		notificationContainer('Regulation added successfully', 'success');
		yield put(
			getGpsrRegulationsRequest({
				emId: payload.emId,
				productTypeId: payload.productTypeId,
			}),
		);
	} catch (error) {
		if (axios.isAxiosError(error)) {
			notificationContainer(String(error?.response?.data?.message ?? error ?? 'Error!'), 'error');
		}
	}
}

function* deleteGpsrRegulationsRequestWorker(action: PayloadAction<TDeleteGpsrRegulationsRequest>) {
	const { payload } = action;
	try {
		yield call(api.gpsr.deleteGpsrRegulations, payload);
		notificationContainer('Regulation deleted successfully', 'success');
		yield put(
			getGpsrRegulationsRequest({
				emId: payload.emId,
				productTypeId: payload.productTypeId,
			}),
		);
		notificationContainer('Regulation deleted successfully', 'success');
	} catch (error) {
		if (axios.isAxiosError(error)) {
			notificationContainer(String(error?.response?.data?.message ?? error ?? 'Error!'), 'error');
		}
	}
}

function* getProductTypeReferencesRequestWorker(
	action: PayloadAction<TGetProductTypeReferencesRequest>,
) {
	const { payload } = action;
	try {
		const response: TGetProductTypeReferencesResponsePayload = yield call(
			api.gpsr.getProductTypeReferences,
			payload,
		);
		yield put(getProductTypeReferencesSuccess(response));
	} catch (error) {
		if (axios.isAxiosError(error)) {
			notificationContainer(String(error?.response?.data?.message ?? error ?? 'Error!'), 'error');
		}
	}
}

function* getProductTypeRegulationsGroupTypesRequestWorker(
	action: PayloadAction<TGetProductTypeRegulationsGroupTypesRequest>,
) {
	const { payload } = action;
	try {
		const response: TGetProductTypeRegulationsGroupTypesResponsePayload = yield call(
			api.gpsr.getProductTypeRegulationsGroupTypes,
			payload,
		);
		yield put(getProductTypeRegulationsGroupTypesSuccess(response));
	} catch (error) {
		if (axios.isAxiosError(error)) {
			notificationContainer(String(error?.response?.data?.message ?? error ?? 'Error!'), 'error');
		}
	}
}

function* getProductTypeRegulationsReferencesRequestWorker(
	action: PayloadAction<TGetProductTypeRegulationsReferencesRequest>,
) {
	const { payload } = action;
	try {
		const response: TGetProductTypeRegulationsReferencesResponsePayload = yield call(
			api.gpsr.getProductTypeRegulationsReferences,
			payload,
		);
		yield put(getProductTypeRegulationsReferencesSuccess(response));
	} catch (error) {
		if (axios.isAxiosError(error)) {
			notificationContainer(String(error?.response?.data?.message ?? error ?? 'Error!'), 'error');
		}
	}
}

function* getRegulationsReferencesRequestWorker(
	action: PayloadAction<TGetRegulationsReferencesRequest>,
) {
	const { payload } = action;
	try {
		const response: TGetRegulationsReferencesResponsePayload = yield call(
			api.gpsr.getRegulationsReferences,
			payload,
		);
		yield put(getRegulationsReferencesSuccess(response));
	} catch (error) {
		if (axios.isAxiosError(error)) {
			notificationContainer(String(error?.response?.data?.message ?? error ?? 'Error!'), 'error');
		}
	}
}

export function* getTechFileRequestWorker(
	action: PayloadAction<TGetTechFileRequest>,
): Generator<any, void, any> {
	const { payload } = action;
	try {
		const response: TGetTechFileResponsePayload = yield call(api.gpsr.getTechFile, payload);
		if (response) {
			fileDownload(response, `tech_file_${payload.emId}_${payload.productTypeId}.pdf`);
		}
		yield put(getTechFileSuccess());
	} catch (error) {
		if (axios.isAxiosError(error)) {
			const responseData = yield error?.response?.data.text().then((data: any) => data);
			notificationContainer(JSON.parse(responseData)?.message ?? 'File download error', 'error');
		}
		yield put(getTechFileSuccess());
	}
}

export function* getDocFileRequestWorker(
	action: PayloadAction<TGetDocFileRequest>,
): Generator<any, void, any> {
	const { payload } = action;
	try {
		const response: TGetDocFileResponsePayload = yield call(api.gpsr.getDocFile, payload);
		if (response) {
			fileDownload(response, `DoC_file_${payload.emId}_${payload.productTypeId}.pdf`);
		}
		yield put(getDocFileSuccess());
	} catch (error) {
		if (axios.isAxiosError(error)) {
			if (error?.response?.status === 404) {
				notificationContainer('Method not found', 'error');
			} else {
				const responseData = yield error?.response?.data.text().then((data: any) => data);
				notificationContainer(JSON.parse(responseData)?.message ?? 'File download error', 'error');
			}
		}
		yield put(getDocFileSuccess());
	}
}

function* cancelGpsrRequestWorker(action: PayloadAction<TCancelGpsrRequest>) {
	const { payload } = action;
	try {
		yield call(api.gpsr.cancelGpsr, payload.id);
		yield put(cancelGpsrSuccess());
		notificationContainer('GPSR cancelled successfully', 'success');
		yield put(getGpsrProductTypesRequest(payload.request));
	} catch (error) {
		if (axios.isAxiosError(error)) {
			notificationContainer(String(error?.response?.data?.message ?? error ?? 'Error!'), 'error');
		}
	}
}

function* activateGpsrRequestWorker(action: PayloadAction<TActivateGpsrRequest>) {
	const { payload } = action;
	try {
		yield call(api.gpsr.activateGpsr, payload.id);
		yield put(activateGpsrSuccess());
		notificationContainer('GPSR activated successfully', 'success');
		yield put(getGpsrProductTypesRequest({ emId: payload.id, searchString: '' }));
	} catch (error) {
		if (axios.isAxiosError(error)) {
			notificationContainer(String(error?.response?.data?.message ?? error ?? 'Error!'), 'error');
		}
	}
}

function* changeGpsrStatusRequestWorker(action: PayloadAction<TChangeGpsrStatusRequest>) {
	const { payload } = action;
	try {
		const response: TChangeGpsrStatusResponsePayload = yield call(
			api.gpsr.changeGpsrStatus,
			payload,
		);
		console.log('changeGpsrStatusRequestWorker response', response);
		if (response) {
			yield put(changeGpsrStatusSuccess());
			notificationContainer(response?.message ?? 'GPSR status changed successfully', 'success');
			yield put(getGpsrProductTypesRequest(payload.request));
		}
	} catch (error) {
		if (axios.isAxiosError(error)) {
			notificationContainer(String(error?.response?.data?.message ?? error ?? 'Error!'), 'error');
		}
	}
}

function* updateGpsrServiceSettingsRequestWorker(
	action: PayloadAction<TUpdateGpsrServiceSettingsRequest>,
): Generator<any, void, any> {
	const { payload } = action;
	try {
		yield call(api.gpsr.updateGpsrServiceSettings, payload);
		yield put(updateGpsrServiceSettingsSuccess());
		notificationContainer('GPSR service settings updated', 'success');
	} catch (error) {
		if (axios.isAxiosError(error)) {
			notificationContainer(String(error?.response?.data?.message ?? error ?? 'Error!'), 'error');
		}
	}
}

function* updateGpsrRemoveOnRenewalRequestWorker(
	action: PayloadAction<TUpdateGpsrRemoveOnRenewalRequest>,
): Generator<any, void, any> {
	const { payload } = action;
	try {
		yield call(api.gpsr.updateGpsrRemoveOnRenewal, payload);
		yield put(updateGpsrRemoveOnRenewalSuccess());
		notificationContainer('GPSR remove on renewal updated', 'success');
		if (payload.emId) {
			yield put(getGpsrProductTypesRequest({ emId: payload.emId, searchString: '' }));
		}
	} catch (error) {
		if (axios.isAxiosError(error)) {
			notificationContainer(String(error?.response?.data?.message ?? error ?? 'Error!'), 'error');
		}
	}
}

function* downloadGpsrLastInvoiceRequestWorker(
	action: PayloadAction<TDownloadGpsrLastInvoiceRequest>,
): Generator<any, void, any> {
	const { payload } = action;
	try {
		const response: TDownloadGpsrLastInvoiceResponsePayload = yield call(
			api.gpsr.downloadGpsrLastInvoice,
			payload,
		);
		if (response) {
			fileDownload(response, `invoice_${payload.id}.pdf`);
		}

		yield put(downloadGpsrLastInvoiceSuccess());
	} catch (error) {
		if (axios.isAxiosError(error)) {
			if (error?.response?.status === 404) {
				notificationContainer('File not found', 'error');
			} else {
				const responseData = yield error?.response?.data.text().then((data: any) => data);
				notificationContainer(JSON.parse(responseData)?.message ?? 'File download error', 'error');
			}
		}
		yield put(downloadGpsrLastInvoiceSuccess());
	}
}

function* getGpsrInvoicesListRequestWorker(action: PayloadAction<TGetInvoicesListRequest>) {
	const { payload } = action;
	try {
		const response: TGetInvoicesListResponsePayload = yield call(
			api.gpsr.getGpsrInvoicesList,
			payload,
		);
		yield put(getGpsrInvoicesListSuccess(response));
	} catch (error) {
		if (axios.isAxiosError(error)) {
			notificationContainer(String(error?.response?.data?.message ?? error ?? 'Error!'), 'error');
		}
	}
}

function* downloadGpsrInvoiceRequestWorker(action: PayloadAction<TDownloadGpsrInvoiceRequest>) {
	const { payload } = action;
	try {
		const response: TDownloadGpsrInvoiceResponsePayload = yield call(
			api.gpsr.downloadGpsrInvoice,
			payload,
		);
		if (response) {
			fileDownload(response, `invoice_${payload.invoiceId}.pdf`);
		}

		yield put(downloadGpsrInvoiceSuccess());
	} catch (error) {
		if (axios.isAxiosError(error)) {
			notificationContainer(String(error?.response?.data?.message ?? error ?? 'Error!'), 'error');
		}
		yield put(downloadGpsrInvoiceSuccess());
	}
}

function* gpsrReferralRequestWorker(action: PayloadAction<TGpsrReferralRequest>) {
	const { payload } = action;
	try {
		yield call(api.gpsr.postGpsrReferral, payload);
		yield put(gpsrReferralSuccess());
		notificationContainer('GPSR referral saved successfully', 'success');
		yield put(
			getGpsrProductTypesRequest({
				emId: payload.emId,
				pageNumber: 1,
				pageSize: PAGE_SIZE,
				searchString: '',
			}),
		);
	} catch (error) {
		if (axios.isAxiosError(error)) {
			notificationContainer(String(error?.response?.data?.message ?? error ?? 'Error!'), 'error');
			yield put(
				gpsrReferralFailure({
					error: String(error?.response?.data?.message ?? error ?? 'Error!'),
				}),
			);
		} else {
			yield put(gpsrReferralFailure({ error: String(error ?? 'Error!') }));
		}
	}
}

export function* getGpsrServiceStatusesRequestWorker() {
	try {
		const response: TGpsrServiceStatusesResponsePayload = yield call(
			api.gpsr.getGpsrServiceStatuses,
		);
		yield put(getGpsrServiceStatusesSuccess(response));
	} catch (error) {
		if (axios.isAxiosError(error)) {
			notificationContainer(String(error?.response?.data?.message ?? error ?? 'Error!'), 'error');
		}
		yield put(getGpsrServiceStatusesFailure());
	}
}

export function* gpsrSaga() {
	yield takeEvery(regParseGpsrRequest.type, regParseGpsrRequestWorker);
	yield takeEvery(registerGpsrRequest.type, registerGpsrRequestWorker);
	yield takeEvery(editGpsrCustomerRequest.type, editGpsrCustomerRequestWorker);
	yield takeEvery(getGpsrPlansRequest.type, getGpsrPlansRequestWorker);
	yield takeEvery(getGpsrItemsRequest.type, getGpsrItemsRequestWorker);
	yield takeEvery(getGpsrProductTypesRequest.type, getGpsrProductTypesRequestWorker);
	yield takeEvery(generateInvoiceGpsrRequest.type, generateInvoiceGpsrRequestWorker);
	yield takeEvery(getGpsrRegulationsRequest.type, getGpsrRegulationsRequestWorker);
	yield takeEvery(addGpsrProductTypeRequest.type, addGpsrProductTypeRequestWorker);
	yield takeEvery(deleteGpsrProductTypeRequest.type, deleteGpsrProductTypeRequestWorker);
	yield takeEvery(addGpsrRegulationsRequest.type, addGpsrRegulationsRequestWorker);
	yield takeEvery(deleteGpsrRegulationsRequest.type, deleteGpsrRegulationsRequestWorker);
	yield takeEvery(getProductTypeReferencesRequest.type, getProductTypeReferencesRequestWorker);
	yield takeEvery(
		getProductTypeRegulationsGroupTypesRequest.type,
		getProductTypeRegulationsGroupTypesRequestWorker,
	);
	yield takeEvery(
		getProductTypeRegulationsReferencesRequest.type,
		getProductTypeRegulationsReferencesRequestWorker,
	);
	yield takeEvery(getRegulationsReferencesRequest.type, getRegulationsReferencesRequestWorker);
	yield takeEvery(getTechFileRequest.type, getTechFileRequestWorker);
	yield takeEvery(getDocFileRequest.type, getDocFileRequestWorker);
	yield takeEvery(cancelGpsrRequest.type, cancelGpsrRequestWorker);
	yield takeEvery(activateGpsrRequest.type, activateGpsrRequestWorker);
	yield takeEvery(downloadGpsrLastInvoiceRequest.type, downloadGpsrLastInvoiceRequestWorker);
	yield takeEvery(getGpsrInvoicesListRequest.type, getGpsrInvoicesListRequestWorker);
	yield takeEvery(downloadGpsrInvoiceRequest.type, downloadGpsrInvoiceRequestWorker);
	yield takeEvery(updateGpsrServiceSettingsRequest.type, updateGpsrServiceSettingsRequestWorker);
	yield takeEvery(updateGpsrRemoveOnRenewalRequest.type, updateGpsrRemoveOnRenewalRequestWorker);
	yield takeEvery(changeGpsrStatusRequest.type, changeGpsrStatusRequestWorker);
	yield takeEvery(getGpsrServiceStatusesRequest.type, getGpsrServiceStatusesRequestWorker);
	yield takeEvery(gpsrReferralRequest.type, gpsrReferralRequestWorker);
}
